import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Receipt, Plus, Eye, Edit2, Trash2, FileText, AlertCircle, CalendarIcon, CheckCircle2, Printer, Send, BookOpen, Building2, FileCheck } from 'lucide-react';
import { formatDate, cn } from '@/lib/utils';
import { format } from 'date-fns';
import { formatCurrency, formatRate } from '@/utils/formatters';
import SRNPrint from '@/components/SRNPrint';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  submitted: 'bg-orange-100 text-orange-700 border-orange-200',
  approved: 'bg-blue-100 text-blue-700 border-blue-200',
  complete: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-red-100 text-red-500 border-red-200'
};

const statusLabels = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  complete: 'Complete',
  cancelled: 'Cancelled'
};

export default function ServicesReceivedNotesPage() {
  const { user, getAuthHeader } = useAuth();
  const [srns, setSrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [spos, setSpos] = useState([]); // Service POs for linking
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [summary, setSummary] = useState({});

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingSrn, setEditingSrn] = useState(null);
  const [formData, setFormData] = useState({
    spo_id: '',
    supplier_id: '',
    supplier_name: '',
    invoice_no: '',
    invoice_date: null,
    invoice_amount: 0,
    service_items: [{ description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }],
    gst_rate: 18,
    other_charges: 0,
    advance_deductions: [],
    total_advance_deducted: 0,
    payment_due_date: null,
    remarks: ''
  });
  const [saving, setSaving] = useState(false);

  // Available advances for selected supplier
  const [availableAdvances, setAvailableAdvances] = useState([]);
  const [loadingAdvances, setLoadingAdvances] = useState(false);

  // View detail dialog
  const [viewingSrn, setViewingSrn] = useState(null);

  // Lifecycle / Print state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [printSrn, setPrintSrn] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [srnsRes, suppliersRes, sposRes] = await Promise.all([
        axios.get(`${API_URL}/services-received-notes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/service-purchase-orders?status=approved`, { headers: getAuthHeader() }).catch(() => ({ data: { spos: [] } }))
      ]);
      setSrns(srnsRes.data.srns || []);
      setSummary(srnsRes.data.summary || {});
      setSuppliers(suppliersRes.data || []);
      // Get both approved and issued SPOs
      const approvedSpos = sposRes.data.spos || [];
      const issuedRes = await axios.get(`${API_URL}/service-purchase-orders?status=issued`, { headers: getAuthHeader() }).catch(() => ({ data: { spos: [] } }));
      setSpos([...approvedSpos, ...(issuedRes.data.spos || [])]);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch available advances when supplier changes
  const fetchAvailableAdvances = async (supplierId) => {
    if (!supplierId) {
      setAvailableAdvances([]);
      return;
    }
    setLoadingAdvances(true);
    try {
      const res = await axios.get(`${API_URL}/services-received-notes/available-advances/${supplierId}`, { headers: getAuthHeader() });
      setAvailableAdvances(res.data.advances || []);
      
      // Auto-populate advance deductions
      const advances = res.data.advances || [];
      if (advances.length > 0) {
        const deductions = advances.map(adv => ({
          spo_id: adv.spo_id,
          spo_number: adv.spo_number,
          amount_deducted: adv.available
        }));
        const totalAdvance = advances.reduce((sum, adv) => sum + adv.available, 0);
        setFormData(prev => ({
          ...prev,
          advance_deductions: deductions,
          total_advance_deducted: totalAdvance
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          advance_deductions: [],
          total_advance_deducted: 0
        }));
      }
    } catch (error) {
      console.error('Failed to fetch advances:', error);
    } finally {
      setLoadingAdvances(false);
    }
  };

  // Date helpers
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('-')) {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    }
    return dateStr;
  };

  const parseDateStringToDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      if (dateStr.includes('-')) {
        return new Date(dateStr);
      }
      return null;
    } catch {
      return null;
    }
  };

  const formatDateForBackend = (date) => {
    if (!date) return null;
    return format(date, 'ddMMyyyy');
  };

  // Calculate totals
  const calculateTotals = (items, gstRate, otherCharges) => {
    const subTotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    const gstAmount = (subTotal * gstRate / 100);
    const calculatedTotal = subTotal + gstAmount + (otherCharges || 0);
    return { subTotal, gstAmount, calculatedTotal };
  };

  // Filtered SRNs
  const filteredSrns = srns.filter(srn => {
    if (filterStatus === 'all') return true;
    return srn.status === filterStatus;
  });

  // Handle form input change
  const handleInputChange = (field, value) => {
    if (typeof value === 'string' && !['supplier_id', 'spo_id', 'invoice_date', 'payment_due_date'].includes(field)) {
      value = value.toUpperCase();
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle service item change
  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.service_items];
    if (field === 'description' || field === 'unit') {
      value = value.toUpperCase();
    }
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    
    setFormData(prev => ({ ...prev, service_items: newItems }));
  };

  // Add service item
  const addServiceItem = () => {
    setFormData(prev => ({
      ...prev,
      service_items: [...prev.service_items, { description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }]
    }));
  };

  // Remove service item
  const removeServiceItem = (index) => {
    if (formData.service_items.length === 1) {
      toast.error('At least one service item is required');
      return;
    }
    const newItems = formData.service_items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, service_items: newItems }));
  };

  // Handle supplier selection
  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setFormData(prev => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: supplier?.name || '',
      spo_id: '' // Reset SPO when supplier changes
    }));
    fetchAvailableAdvances(supplierId);
  };

  // Handle SPO selection (optional link)
  const handleSpoChange = (spoId) => {
    const spo = spos.find(s => s.id === spoId);
    if (spo) {
      // Auto-fill from SPO
      setFormData(prev => ({
        ...prev,
        spo_id: spoId,
        supplier_id: spo.supplier_id,
        supplier_name: spo.supplier_name,
        service_items: spo.service_items || prev.service_items,
        gst_rate: spo.gst_rate || 18,
        other_charges: spo.other_charges || 0
      }));
      fetchAvailableAdvances(spo.supplier_id);
    } else {
      setFormData(prev => ({ ...prev, spo_id: '' }));
    }
  };

  // Handle advance deduction change
  const handleAdvanceDeductionChange = (spoId, amount) => {
    const newDeductions = [...formData.advance_deductions];
    const existingIndex = newDeductions.findIndex(d => d.spo_id === spoId);
    const advance = availableAdvances.find(a => a.spo_id === spoId);
    
    if (existingIndex >= 0) {
      if (amount <= 0) {
        newDeductions.splice(existingIndex, 1);
      } else {
        newDeductions[existingIndex].amount_deducted = Math.min(amount, advance?.available || 0);
      }
    } else if (amount > 0 && advance) {
      newDeductions.push({
        spo_id: spoId,
        spo_number: advance.spo_number,
        amount_deducted: Math.min(amount, advance.available)
      });
    }
    
    const totalAdvance = newDeductions.reduce((sum, d) => sum + d.amount_deducted, 0);
    setFormData(prev => ({
      ...prev,
      advance_deductions: newDeductions,
      total_advance_deducted: totalAdvance
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      spo_id: '',
      supplier_id: '',
      supplier_name: '',
      invoice_no: '',
      invoice_date: null,
      invoice_amount: 0,
      service_items: [{ description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }],
      gst_rate: 18,
      other_charges: 0,
      advance_deductions: [],
      total_advance_deducted: 0,
      payment_due_date: null,
      remarks: ''
    });
    setEditingSrn(null);
    setAvailableAdvances([]);
  };

  // Open form for create
  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  // Open form for edit
  const openEditForm = (srn) => {
    setEditingSrn(srn);
    setFormData({
      spo_id: srn.spo_id || '',
      supplier_id: srn.supplier_id,
      supplier_name: srn.supplier_name,
      invoice_no: srn.invoice_no,
      invoice_date: parseDateStringToDate(srn.invoice_date),
      invoice_amount: srn.invoice_amount,
      service_items: srn.service_items || [{ description: '', quantity: '', unit: 'NOS', rate: 0, amount: 0 }],
      gst_rate: srn.gst_rate || 18,
      other_charges: srn.other_charges || 0,
      advance_deductions: srn.advance_deductions || [],
      total_advance_deducted: srn.total_advance_deducted || 0,
      payment_due_date: parseDateStringToDate(srn.payment_due_date),
      remarks: srn.remarks || ''
    });
    fetchAvailableAdvances(srn.supplier_id);
    setShowForm(true);
  };

  // Submit form
  const handleSubmit = async () => {
    // Validation
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.invoice_no) {
      toast.error('Please enter invoice number');
      return;
    }
    if (!formData.invoice_date) {
      toast.error('Please select invoice date');
      return;
    }
    // Validate quantities
    for (let i = 0; i < formData.service_items.length; i++) {
      const item = formData.service_items[i];
      const qty = parseFloat(item.quantity);
      if (!item.quantity || isNaN(qty) || qty <= 0) {
        toast.error(`Service item ${i + 1}: Quantity is required`);
        return;
      }
    }
    if (formData.service_items.some(item => !item.description || item.rate <= 0)) {
      toast.error('Please fill all service items with valid rates');
      return;
    }

    // Auto-calculate invoice amount from service items
    const invoiceAmount = calculatedTotal;
    const balanceAmount = invoiceAmount - formData.total_advance_deducted;
    if (balanceAmount > 0 && !formData.payment_due_date) {
      toast.error('Please select payment due date for balance amount');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        invoice_amount: invoiceAmount,  // Use calculated total
        invoice_date: formatDateForBackend(formData.invoice_date),
        payment_due_date: formatDateForBackend(formData.payment_due_date),
        service_items: formData.service_items.map(item => ({
          ...item,
          amount: item.quantity * item.rate
        }))
      };

      if (editingSrn) {
        await axios.patch(`${API_URL}/services-received-notes/${editingSrn.id}`, payload, { headers: getAuthHeader() });
        toast.success('SRN updated successfully');
      } else {
        const response = await axios.post(`${API_URL}/services-received-notes`, payload, { headers: getAuthHeader() });
        if (response.data.balance_payment_request_id) {
          toast.success('SRN created. Balance payment request submitted for approval.');
        } else {
          toast.success('SRN created successfully');
        }
      }

      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SRN');
    } finally {
      setSaving(false);
    }
  };

  // Delete SRN
  const handleDelete = async (srn) => {
    if (!confirm(`Are you sure you want to delete SRN ${srn.srn_number}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/services-received-notes/${srn.id}`, { headers: getAuthHeader() });
      toast.success('SRN deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete SRN');
    }
  };

  // ============= DOCUMENT LIFECYCLE HANDLERS =============

  const handleInvoiceReceived = async (srnId) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API_URL}/services-received-notes/${srnId}/invoice-status`,
        { invoice_received: true },
        { headers: getAuthHeader() }
      );
      toast.success('Invoice marked as received');
      fetchData();
      if (viewingSrn && viewingSrn.id === srnId) {
        setViewingSrn(prev => ({
          ...prev,
          invoice_received: true,
          invoice_received_at: new Date().toISOString(),
          invoice_received_by: user?.name || user?.email
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDocsHandover = async (srnId) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API_URL}/services-received-notes/${srnId}/docs-handover`,
        { docs_handed_over: true },
        { headers: getAuthHeader() }
      );
      toast.success('Documents marked as handed over to Accounts');
      fetchData();
      if (viewingSrn && viewingSrn.id === srnId) {
        setViewingSrn(prev => ({
          ...prev,
          docs_handed_over: true,
          docs_handed_over_at: new Date().toISOString(),
          docs_handed_over_by: user?.name || user?.email
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update docs handover status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDocsReceived = async (srnId) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API_URL}/services-received-notes/${srnId}/docs-received`,
        { docs_received: true },
        { headers: getAuthHeader() }
      );
      toast.success('Documents acknowledged as received by Accounts');
      fetchData();
      if (viewingSrn && viewingSrn.id === srnId) {
        setViewingSrn(prev => ({
          ...prev,
          docs_received: true,
          docs_received_at: new Date().toISOString(),
          docs_received_by: user?.name || user?.email
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update docs received status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleTallyRecorded = async (srnId) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API_URL}/services-received-notes/${srnId}/tally-recorded`,
        { tally_recorded: true },
        { headers: getAuthHeader() }
      );
      toast.success('Invoice marked as recorded in Tally');
      fetchData();
      if (viewingSrn && viewingSrn.id === srnId) {
        setViewingSrn(prev => ({
          ...prev,
          tally_recorded: true,
          tally_recorded_at: new Date().toISOString(),
          tally_recorded_by: user?.name || user?.email
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Tally status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Totals for current form
  const { subTotal, gstAmount, calculatedTotal } = calculateTotals(
    formData.service_items,
    formData.gst_rate,
    formData.other_charges
  );

  // Invoice amount is always the calculated total
  const invoiceAmount = calculatedTotal;
  const balanceAmount = invoiceAmount - formData.total_advance_deducted;

  // Get SPOs filtered by selected supplier
  const filteredSpos = spos.filter(spo => !formData.supplier_id || spo.supplier_id === formData.supplier_id);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Receipt className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Services Received Notes (SRN)</h1>
              <p className="text-sm text-gray-500">Record service invoices and manage payments</p>
            </div>
          </div>
          <Button onClick={openCreateForm} className="gap-2">
            <Plus className="w-4 h-4" />
            Create SRN
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('all')}>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-900">{summary.total || 0}</div>
              <div className="text-sm text-gray-500">Total SRNs</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('submitted')}>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{summary.submitted || 0}</div>
              <div className="text-sm text-gray-500">Pending Approval</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_invoice_amount || 0)}</div>
              <div className="text-sm text-gray-500">Total Invoice Value</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_advance_deducted || 0)}</div>
              <div className="text-sm text-gray-500">Advances Adjusted</div>
            </CardContent>
          </Card>
        </div>

        {/* SRN Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">SRN Register</CardTitle>
              {filterStatus !== 'all' && (
                <Button variant="ghost" size="sm" onClick={() => setFilterStatus('all')}>
                  Clear Filter
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : filteredSrns.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No service invoices found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SRN No.</TableHead>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>SPO Ref.</TableHead>
                      <TableHead className="text-right">Invoice Amt</TableHead>
                      <TableHead className="text-right">Advance</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSrns.map((srn) => (
                      <TableRow key={srn.id}>
                        <TableCell className="font-medium">{srn.srn_number}</TableCell>
                        <TableCell>{srn.invoice_no}</TableCell>
                        <TableCell>{formatDateForDisplay(srn.invoice_date)}</TableCell>
                        <TableCell>{srn.supplier_name}</TableCell>
                        <TableCell>{srn.spo_number || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(srn.invoice_amount || 0)}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {srn.total_advance_deducted > 0 ? formatCurrency(srn.total_advance_deducted) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium text-orange-600">
                          {formatCurrency(srn.balance_amount || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[srn.status]} border`}>
                            {statusLabels[srn.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setViewingSrn(srn)} title="View Details">
                              <Eye className="w-4 h-4" />
                            </Button>
                            {['draft', 'submitted'].includes(srn.status) && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => openEditForm(srn)} title="Edit">
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(srn)} title="Delete" className="text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Form Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); resetForm(); } }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSrn ? 'Edit SRN' : 'Create Service Invoice Entry (SRN)'}</DialogTitle>
              <DialogDescription>
                {editingSrn ? 'Update service invoice details' : 'Record a service invoice and create payment request'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* SPO Link (Optional) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Link to Service PO (Optional)</Label>
                  <SearchableSelect
                    options={filteredSpos.map(s => ({ value: s.id, label: `${s.spo_number} - ${s.supplier_name}` }))}
                    value={formData.spo_id}
                    onValueChange={handleSpoChange}
                    placeholder="Select Service PO"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Supplier *</Label>
                  <SearchableSelect
                    options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                    value={formData.supplier_id}
                    onValueChange={handleSupplierChange}
                    placeholder="Select supplier"
                  />
                </div>
              </div>

              {/* Invoice Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Invoice Number *</Label>
                  <Input
                    value={formData.invoice_no}
                    onChange={(e) => handleInputChange('invoice_no', e.target.value)}
                    placeholder="INVOICE NUMBER"
                    className="uppercase"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Invoice Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.invoice_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.invoice_date ? format(formData.invoice_date, 'dd/MM/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.invoice_date}
                        onSelect={(date) => handleInputChange('invoice_date', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Service Items */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Service Items</Label>
                  <Button variant="outline" size="sm" onClick={addServiceItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40%]">Description</TableHead>
                        <TableHead className="w-[12%]">Qty</TableHead>
                        <TableHead className="w-[12%]">Unit</TableHead>
                        <TableHead className="w-[15%]">Rate</TableHead>
                        <TableHead className="w-[15%] text-right">Amount</TableHead>
                        <TableHead className="w-[6%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.service_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Input
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              placeholder="Service description"
                              className="uppercase"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              placeholder="NOS"
                              className="uppercase"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.quantity * item.rate)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeServiceItem(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Calculated Totals (Reference) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>GST Rate (%)</Label>
                  <Input
                    type="number"
                    value={formData.gst_rate}
                    onChange={(e) => handleInputChange('gst_rate', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Other Charges (Non-Taxable)</Label>
                  <Input
                    type="number"
                    value={formData.other_charges}
                    onChange={(e) => handleInputChange('other_charges', parseFloat(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="space-y-1 bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span>Sub Total:</span>
                    <span>{formatCurrency(subTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({formData.gst_rate}%):</span>
                    <span>{formatCurrency(gstAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Charges:</span>
                    <span>{formatCurrency(formData.other_charges || 0)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>Calculated:</span>
                    <span>{formatCurrency(calculatedTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Available Advances */}
              {formData.supplier_id && (
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Available Advances from Service POs</Label>
                    {loadingAdvances && <span className="text-sm text-gray-500">Loading...</span>}
                  </div>
                  {availableAdvances.length > 0 ? (
                    <div className="space-y-3">
                      {availableAdvances.map((advance) => {
                        const deduction = formData.advance_deductions.find(d => d.spo_id === advance.spo_id);
                        const currentAmount = deduction?.amount_deducted || 0;
                        
                        return (
                          <div key={advance.spo_id} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                            <div>
                              <p className="font-medium">{advance.spo_number}</p>
                              <p className="text-sm text-gray-500">{advance.service_description}</p>
                              <p className="text-sm text-green-600">Available: {formatCurrency(advance.available)}</p>
                            </div>
                            <div className="space-y-1 text-right">
                              <Label className="text-sm">Deduct Amount</Label>
                              <Input
                                type="number"
                                value={currentAmount}
                                onChange={(e) => handleAdvanceDeductionChange(advance.spo_id, parseFloat(e.target.value) || 0)}
                                max={advance.available}
                                min="0"
                                className="w-32"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No advances available for this supplier</p>
                  )}
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <Label className="font-medium text-blue-800">Payment Summary</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Invoice Amount:</span>
                    <p className="text-lg font-bold">{formatCurrency(invoiceAmount)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Advance Deducted:</span>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(formData.total_advance_deducted)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Balance Payment:</span>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(balanceAmount)}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Payment Due Date {balanceAmount > 0 && '*'}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          disabled={balanceAmount <= 0}
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.payment_due_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.payment_due_date ? format(formData.payment_due_date, 'dd/MM/yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.payment_due_date}
                          onSelect={(date) => handleInputChange('payment_due_date', date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                {balanceAmount > 0 && (
                  <div className="flex items-start gap-2 text-sm text-orange-700 bg-orange-100 p-3 rounded">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <span>A payment request for {formatCurrency(balanceAmount)} will be automatically created and sent for owner approval.</span>
                  </div>
                )}
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Textarea
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  placeholder="Any additional remarks..."
                  className="uppercase"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : (editingSrn ? 'Update SRN' : 'Create SRN')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Details Dialog — GRN-style layout with lifecycle */}
        <Dialog open={!!viewingSrn} onOpenChange={(open) => { if (!open) setViewingSrn(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-600" />
                SRN Details
              </DialogTitle>
            </DialogHeader>
            
            {viewingSrn && (
              <div className="space-y-6 py-4">
                {/* SRN Header Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">SRN Number</p>
                    <p className="font-mono font-bold text-lg" data-testid="srn-detail-number">{viewingSrn.srn_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Supplier</p>
                    <p className="font-medium">{viewingSrn.supplier_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Invoice No.</p>
                    <p className="font-medium">{viewingSrn.invoice_no}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Invoice Date</p>
                    <p className="font-medium">{formatDateForDisplay(viewingSrn.invoice_date)}</p>
                  </div>
                  {viewingSrn.spo_number && (
                    <div>
                      <p className="text-xs text-muted-foreground">Linked SPO</p>
                      <p className="font-medium">{viewingSrn.spo_number}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={`${statusColors[viewingSrn.status]} border mt-1`}>
                      {statusLabels[viewingSrn.status]}
                    </Badge>
                  </div>
                </div>

                {/* Service Items */}
                <div>
                  <Label className="text-gray-500 mb-2 block">Service Items</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingSrn.service_items?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell>{item.unit}</TableCell>
                            <TableCell className="text-right">{formatRate(item.rate)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Invoice & Document Workflow Section */}
                <div className="space-y-3" data-testid="srn-lifecycle-section">
                  <h4 className="font-semibold text-sm">Invoice & Document Workflow</h4>
                  
                  {/* Step 1: Invoice Received */}
                  <div className={`p-4 rounded-lg border ${viewingSrn.invoice_received ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="srn-lifecycle-invoice">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${viewingSrn.invoice_received ? 'bg-green-100' : 'bg-amber-100'}`}>
                          <FileCheck className={`w-5 h-5 ${viewingSrn.invoice_received ? 'text-green-600' : 'text-amber-600'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${viewingSrn.invoice_received ? 'text-green-800' : 'text-amber-800'}`}>
                            Invoice {viewingSrn.invoice_received ? 'Received' : 'Pending'}
                          </p>
                          {viewingSrn.invoice_received && viewingSrn.invoice_received_at && (
                            <p className="text-xs text-green-600">
                              {formatDate(viewingSrn.invoice_received_at)} {new Date(viewingSrn.invoice_received_at).toLocaleTimeString('en-IN')}
                              {viewingSrn.invoice_received_by && ` \u2022 ${viewingSrn.invoice_received_by}`}
                            </p>
                          )}
                        </div>
                      </div>
                      {!viewingSrn.invoice_received && (
                        <Button
                          size="sm"
                          onClick={() => handleInvoiceReceived(viewingSrn.id)}
                          disabled={isUpdatingStatus}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="srn-btn-invoice-received"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Received
                        </Button>
                      )}
                      {viewingSrn.invoice_received && (
                        <Badge variant="outline" className="text-green-600 border-green-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Step 2: Docs Handover to Accounts */}
                  <div className={`p-4 rounded-lg border ${
                    viewingSrn.docs_handed_over 
                      ? 'bg-blue-50 border-blue-200' 
                      : viewingSrn.invoice_received 
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`} data-testid="srn-lifecycle-handover">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${viewingSrn.docs_handed_over ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <Send className={`w-5 h-5 ${viewingSrn.docs_handed_over ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${viewingSrn.docs_handed_over ? 'text-blue-800' : 'text-gray-600'}`}>
                            Docs Handover to Accounts
                          </p>
                          {viewingSrn.docs_handed_over && viewingSrn.docs_handed_over_at && (
                            <p className="text-xs text-blue-600">
                              {formatDate(viewingSrn.docs_handed_over_at)} {new Date(viewingSrn.docs_handed_over_at).toLocaleTimeString('en-IN')}
                              {viewingSrn.docs_handed_over_by && ` \u2022 ${viewingSrn.docs_handed_over_by}`}
                            </p>
                          )}
                          {!viewingSrn.docs_handed_over && !viewingSrn.invoice_received && (
                            <p className="text-xs text-gray-400">Waiting for invoice to be received</p>
                          )}
                        </div>
                      </div>
                      {viewingSrn.invoice_received && !viewingSrn.docs_handed_over && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDocsHandover(viewingSrn.id)}
                          disabled={isUpdatingStatus}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          data-testid="srn-btn-docs-handover"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Handover Docs
                        </Button>
                      )}
                      {viewingSrn.docs_handed_over && (
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Step 3: Accounts Docs Received */}
                  <div className={`p-4 rounded-lg border ${
                    viewingSrn.docs_received 
                      ? 'bg-indigo-50 border-indigo-200' 
                      : viewingSrn.docs_handed_over 
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`} data-testid="srn-lifecycle-docs-received">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${viewingSrn.docs_received ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                          <Building2 className={`w-5 h-5 ${viewingSrn.docs_received ? 'text-indigo-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${viewingSrn.docs_received ? 'text-indigo-800' : 'text-gray-600'}`}>
                            Accounts: Docs Received
                          </p>
                          {viewingSrn.docs_received && viewingSrn.docs_received_at && (
                            <p className="text-xs text-indigo-600">
                              {formatDate(viewingSrn.docs_received_at)} {new Date(viewingSrn.docs_received_at).toLocaleTimeString('en-IN')}
                              {viewingSrn.docs_received_by && ` \u2022 ${viewingSrn.docs_received_by}`}
                            </p>
                          )}
                          {!viewingSrn.docs_received && viewingSrn.docs_handed_over && (
                            <p className="text-xs text-gray-500">Pending acknowledgement in Accounts</p>
                          )}
                          {!viewingSrn.docs_received && !viewingSrn.docs_handed_over && (
                            <p className="text-xs text-gray-400">Waiting for docs handover</p>
                          )}
                        </div>
                      </div>
                      {viewingSrn.docs_handed_over && !viewingSrn.docs_received && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDocsReceived(viewingSrn.id)}
                          disabled={isUpdatingStatus}
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          data-testid="srn-btn-docs-received"
                        >
                          <Building2 className="w-4 h-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                      {viewingSrn.docs_received && (
                        <Badge variant="outline" className="text-indigo-600 border-indigo-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Acknowledged
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Step 4: Recorded in Tally */}
                  <div className={`p-4 rounded-lg border ${
                    viewingSrn.tally_recorded 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : viewingSrn.docs_received 
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`} data-testid="srn-lifecycle-tally">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${viewingSrn.tally_recorded ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                          <BookOpen className={`w-5 h-5 ${viewingSrn.tally_recorded ? 'text-emerald-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${viewingSrn.tally_recorded ? 'text-emerald-800' : 'text-gray-600'}`}>
                            Recorded in Tally
                          </p>
                          {viewingSrn.tally_recorded && viewingSrn.tally_recorded_at && (
                            <p className="text-xs text-emerald-600">
                              {formatDate(viewingSrn.tally_recorded_at)} {new Date(viewingSrn.tally_recorded_at).toLocaleTimeString('en-IN')}
                              {viewingSrn.tally_recorded_by && ` \u2022 ${viewingSrn.tally_recorded_by}`}
                            </p>
                          )}
                          {!viewingSrn.tally_recorded && viewingSrn.docs_received && (
                            <p className="text-xs text-gray-500">Pending tally entry in Accounts</p>
                          )}
                          {!viewingSrn.tally_recorded && !viewingSrn.docs_received && (
                            <p className="text-xs text-gray-400">Waiting for docs acknowledgement</p>
                          )}
                        </div>
                      </div>
                      {viewingSrn.docs_received && !viewingSrn.tally_recorded && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTallyRecorded(viewingSrn.id)}
                          disabled={isUpdatingStatus}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid="srn-btn-tally-recorded"
                        >
                          <BookOpen className="w-4 h-4 mr-1" />
                          Mark Recorded
                        </Button>
                      )}
                      {viewingSrn.tally_recorded && (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span>Sub Total:</span>
                    <span>{formatCurrency(viewingSrn.sub_total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({viewingSrn.gst_rate || 0}%):</span>
                    <span>{formatCurrency(viewingSrn.gst_amount)}</span>
                  </div>
                  {(viewingSrn.other_charges || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Other Charges:</span>
                      <span>{formatCurrency(viewingSrn.other_charges)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium border-t pt-2">
                    <span>Invoice Amount:</span>
                    <span>{formatCurrency(viewingSrn.invoice_amount)}</span>
                  </div>
                  {viewingSrn.total_advance_deducted > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Less: Advance Deducted:</span>
                      <span>- {formatCurrency(viewingSrn.total_advance_deducted)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2 text-orange-600">
                    <span>Balance Payment:</span>
                    <span>{formatCurrency(viewingSrn.balance_amount)}</span>
                  </div>
                  {viewingSrn.payment_due_date && (
                    <div className="flex justify-between text-sm">
                      <span>Due Date:</span>
                      <span>{formatDateForDisplay(viewingSrn.payment_due_date)}</span>
                    </div>
                  )}
                </div>

                {/* Advance Deductions Breakdown */}
                {viewingSrn.advance_deductions?.length > 0 && (
                  <div>
                    <Label className="text-gray-500 mb-2 block">Advance Deductions</Label>
                    <div className="space-y-2">
                      {viewingSrn.advance_deductions.map((ad, index) => (
                        <div key={index} className="flex justify-between bg-green-50 p-2 rounded">
                          <span>{ad.spo_number}</span>
                          <span className="font-medium">{formatCurrency(ad.amount_deducted)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Remarks */}
                {viewingSrn.remarks && (
                  <div>
                    <Label className="text-gray-500">Remarks</Label>
                    <p>{viewingSrn.remarks}</p>
                  </div>
                )}

                {/* Audit Info */}
                <div className="text-sm text-gray-500 border-t pt-4 space-y-1">
                  <p>Created by: {viewingSrn.created_by_name} on {formatDate(viewingSrn.created_at)}</p>
                </div>
              </div>
            )}

            <DialogFooter className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => {
                  if (viewingSrn) {
                    setPrintSrn(viewingSrn);
                  }
                }}
                className="text-teal-600 border-teal-200 hover:bg-teal-50"
                data-testid="srn-btn-print"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print SRN
              </Button>
              <Button variant="outline" onClick={() => setViewingSrn(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SRN Print Component */}
        {printSrn && (
          <SRNPrint 
            srn={printSrn} 
            autoPrint={true}
            onPrintComplete={() => setPrintSrn(null)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
